import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, ClipboardCheck, FileHeart, Info, Save } from 'lucide-react';
import { supabase } from '../../../config/supabase';
import {
  ANAMNESIS_SECTION_DEFINITIONS,
  createEmptyAnamnesisSections,
  hasAnamnesisContent,
  type AnamnesisSectionKey,
  type AnamnesisSections,
} from '../../../utils/clinicalDocuments';
import {
  INITIAL_ASSESSMENT_SCHEMA_VERSION,
  INITIAL_ASSESSMENT_MOMENT_LABELS,
  formatInitialAssessmentDate,
  getInitialAssessmentSourceIssues,
  type InitialAssessmentMoment,
  type NormalizedInitialAssessmentPayload,
  normalizeInitialAssessmentPayload,
  type InitialAssessmentResultPayload,
} from '../initial-assessment/initialAssessmentDomain';
import {
  buildInitialAssessmentAnamnesisOptions,
  type InitialAssessmentAnamnesisOption,
  type InitialAssessmentAnamnesisSourceRecord,
} from '../initial-assessment/initialAssessmentAnamnesis';

import type { AssessmentReportSourceSnapshotV2 } from '../reports/assessmentReportDomain';
import type { AssessmentResultV2 } from '../types';
import { getAssessmentV2Label } from '../registry/assessmentRegistry';
import InitialAssessmentTestPlanning from '../initial-assessment/InitialAssessmentTestPlanning';
import InitialAssessmentExternalExamsEditor from '../initial-assessment/InitialAssessmentExternalExamsEditor';
import { InitialAssessmentGoalsEditor, InitialAssessmentPhysicalExaminationEditor } from '../initial-assessment/InitialAssessmentStructuredFields';
import InitialAssessmentResultRenderer from '../renderers/InitialAssessmentResultRenderer';

type InitialAssessmentFormState = Omit<
  NormalizedInitialAssessmentPayload,
  'schemaVersion' | 'finishedAt' | 'patientContext' | 'professionalContext'
>;

interface InitialAssessmentTestProps {
  initial?: InitialAssessmentResultPayload | null;
  patientId?: string | null;
  clientId?: string | null;
  isEditing?: boolean;
  resultId?: string | null;
  sourceResults?: AssessmentResultV2[];
  sourceResultsLoading?: boolean;
  buildSourceSnapshot?: (result: AssessmentResultV2) => AssessmentReportSourceSnapshotV2;
  onSaveDraft?: (result: InitialAssessmentResultPayload) => void | Promise<void>;
  initialAnamnesisRecordId?: string;
  patientContext?: { ageYears?: number | null };
  onRequestTests?: (keys: string[]) => void;
  onCancel?: () => void;
  onCreateAnamnesis?: (
    input: InitialAssessmentAnamnesisCreationRequest,
  ) => Promise<InitialAssessmentAnamnesisOption>;
  onNext: (result: InitialAssessmentResultPayload) => void;
}

export interface InitialAssessmentAnamnesisCreationRequest {
  recordDate: string;
  sections: AnamnesisSections;
}

const EMPTY_FORM: InitialAssessmentFormState = {
  assessmentMoment: 'initial',
  episodeLabel: '',
  baselineResultId: null,
  previousFunction: '',
  physicalExamination: [],
  measurableGoals: [],
  prognosis: '',
  followUpPlan: '',
  closureReason: '',
  selectedResults: [],
  externalExams: [],
  assessmentReason: '',
  referralDiagnosis: '',
  currentClinicalCondition: '',
  mainSymptoms: '',
  functionalLimitations: '',
  assistiveDevices: '',
  precautions: '',
  patientGoals: '',
  professionalSynthesis: '',
  initialPlan: '',
  additionalNotes: '',
  anamnesisSnapshot: null,
};

const toFormState = (initial?: InitialAssessmentResultPayload | null): InitialAssessmentFormState => {
  const normalized = normalizeInitialAssessmentPayload(initial);
  if (!normalized) return { ...EMPTY_FORM };
  const fields = { ...normalized };
  Reflect.deleteProperty(fields, 'schemaVersion');
  Reflect.deleteProperty(fields, 'finishedAt');
  Reflect.deleteProperty(fields, 'patientContext');
  Reflect.deleteProperty(fields, 'professionalContext');
  return fields;
};

const textAreaClassName =
  'mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100';

const toLocalDateTimeInput = (value?: string): string => {
  const parsed = value ? new Date(value) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const InitialAssessmentTest = ({
  initial = null,
  patientId,
  clientId,
  isEditing = false,
  resultId,
  sourceResults,
  sourceResultsLoading = false,
  buildSourceSnapshot,
  onSaveDraft,
  initialAnamnesisRecordId,
  patientContext,
  onRequestTests,
  onCancel,
  onCreateAnamnesis,
  onNext,
}: InitialAssessmentTestProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sourceSearch, setSourceSearch] = useState('');
  const [requestedTests, setRequestedTests] = useState<string[]>([]);
  const [form, setForm] = useState<InitialAssessmentFormState>(() => toFormState(initial));
  const formRef = useRef(form);
  formRef.current = form;
  const [anamnesisOptions, setAnamnesisOptions] = useState<InitialAssessmentAnamnesisOption[]>([]);
  const [loadingAnamnesis, setLoadingAnamnesis] = useState(false);
  const [anamnesisLoadError, setAnamnesisLoadError] = useState(false);
  const [requestedAnamnesisUnavailable, setRequestedAnamnesisUnavailable] = useState(false);
  const [anamnesisReloadKey, setAnamnesisReloadKey] = useState(0);
  const [showAnamnesisCreator, setShowAnamnesisCreator] = useState(false);
  const [newAnamnesisDate, setNewAnamnesisDate] = useState(() => toLocalDateTimeInput());
  const [newAnamnesisSections, setNewAnamnesisSections] = useState<AnamnesisSections>(
    createEmptyAnamnesisSections,
  );
  const [savingAnamnesis, setSavingAnamnesis] = useState(false);
  const [anamnesisCreationError, setAnamnesisCreationError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const shouldAutoSelectAnamnesis = !initial;

  useEffect(() => {
    if (!patientId || !clientId) {
      setAnamnesisOptions([]);
      return;
    }
    let active = true;

    const loadAnamnesisOptions = async () => {
      setLoadingAnamnesis(true);
      setAnamnesisLoadError(false);
      try {
        const { data, error } = await supabase
          .from('medical_records')
          .select('recordId, recordDate, recordTitle, recordDescription, recordType, recordAttachments')
          .eq('clientId', clientId)
          .eq('patientId', patientId)
          .order('recordDate', { ascending: false });

        if (error) throw error;
        const options = buildInitialAssessmentAnamnesisOptions(
          (data || []) as InitialAssessmentAnamnesisSourceRecord[],
        );
        if (!active) return;

        setAnamnesisOptions(options);
        const requested = initialAnamnesisRecordId
          ? options.find((option) => option.snapshot.sourceRecordId === initialAnamnesisRecordId)
          : undefined;
        setRequestedAnamnesisUnavailable(Boolean(initialAnamnesisRecordId && !requested));
        if (requested || (!initialAnamnesisRecordId && shouldAutoSelectAnamnesis && options[0])) {
          const selected = requested ?? options[0];
          setForm((current) => current.anamnesisSnapshot
            ? current
            : { ...current, anamnesisSnapshot: selected.snapshot });
        }
      } catch (error) {
        console.error('Erro ao carregar anamnese para a avaliação inicial:', error);
        if (active) {
          setAnamnesisOptions([]);
          setAnamnesisLoadError(true);
        }
      } finally {
        if (active) setLoadingAnamnesis(false);
      }
    };

    void loadAnamnesisOptions();
    return () => {
      active = false;
    };
  }, [anamnesisReloadKey, clientId, patientId, shouldAutoSelectAnamnesis, initialAnamnesisRecordId]);

  const displayedAnamnesisOptions = useMemo(() => {
    const selectedSnapshot = form.anamnesisSnapshot;
    if (
      !selectedSnapshot ||
      anamnesisOptions.some((option) => option.snapshot.sourceRecordId === selectedSnapshot.sourceRecordId)
    ) {
      return anamnesisOptions;
    }

    const fallbackTitle = selectedSnapshot.sourceRecordTitle || 'Anamnese selecionada';
    return [
      {
        snapshot: selectedSnapshot,
        label: `${fallbackTitle} • versão ${selectedSnapshot.documentVersion} • ${formatInitialAssessmentDate(selectedSnapshot.sourceRecordDate)}`,
      },
      ...anamnesisOptions,
    ];
  }, [anamnesisOptions, form.anamnesisSnapshot]);

  const anamnesisEntries = useMemo(() => {
    if (!form.anamnesisSnapshot) return [];
    return ANAMNESIS_SECTION_DEFINITIONS
      .map(({ key, label }) => ({ label, value: form.anamnesisSnapshot?.sections[key]?.trim() || '' }))
      .filter(({ value }) => Boolean(value));
  }, [form.anamnesisSnapshot]);

  const update = <K extends keyof InitialAssessmentFormState>(field: K, value: InitialAssessmentFormState[K]) => {
    setValidationError(null);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const selectAnamnesis = (sourceRecordId: string) => {
    setRequestedAnamnesisUnavailable(false);
    const selected = displayedAnamnesisOptions.find(
      (option) => option.snapshot.sourceRecordId === sourceRecordId,
    );
    setForm((current) => ({
      ...current,
      anamnesisSnapshot: selected?.snapshot ?? null,
    }));
  };

  const updateNewAnamnesisSection = (key: AnamnesisSectionKey, value: string) => {
    setAnamnesisCreationError(null);
    setNewAnamnesisSections((current) => ({ ...current, [key]: value }));
  };

  const closeAnamnesisCreator = () => {
    if (savingAnamnesis) return;
    setShowAnamnesisCreator(false);
    setAnamnesisCreationError(null);
    setNewAnamnesisDate(toLocalDateTimeInput());
    setNewAnamnesisSections(createEmptyAnamnesisSections());
  };

  const saveNewAnamnesis = async () => {
    if (!onCreateAnamnesis) return;
    if (!newAnamnesisDate || Number.isNaN(new Date(newAnamnesisDate).getTime())) {
      setAnamnesisCreationError('Informe uma data válida para a anamnese.');
      return;
    }
    if (!hasAnamnesisContent(newAnamnesisSections)) {
      setAnamnesisCreationError('Preencha pelo menos uma seção da anamnese.');
      return;
    }

    setSavingAnamnesis(true);
    setAnamnesisCreationError(null);
    try {
      const createdOption = await onCreateAnamnesis({
        recordDate: new Date(newAnamnesisDate).toISOString(),
        sections: newAnamnesisSections,
      });
      setAnamnesisOptions((current) => [
        createdOption,
        ...current.filter(
          (option) => option.snapshot.sourceRecordId !== createdOption.snapshot.sourceRecordId,
        ),
      ]);
      const linkedForm = { ...formRef.current, anamnesisSnapshot: createdOption.snapshot };
      setForm(linkedForm);
      setAnamnesisLoadError(false);
      setShowAnamnesisCreator(false);
      setNewAnamnesisDate(toLocalDateTimeInput());
      setNewAnamnesisSections(createEmptyAnamnesisSections());
      if (onSaveDraft) {
        try {
          await onSaveDraft({ schemaVersion: INITIAL_ASSESSMENT_SCHEMA_VERSION, ...linkedForm, finishedAt: new Date().toISOString() });
        } catch (error) {
          setValidationError(error instanceof Error ? error.message : 'A anamnese foi registrada. Salve o rascunho para concluir seu vínculo com a avaliação.');
        }
      }
    } catch (error) {
      console.error('Erro ao criar anamnese pela avaliação inicial:', error);
      setAnamnesisCreationError(
        error instanceof Error ? error.message : 'Não foi possível salvar a anamnese no prontuário.',
      );
    } finally {
      setSavingAnamnesis(false);
    }
  };

  const eligibleResults = useMemo(() => (sourceResults ?? []).filter((result) =>
    result.clientId === clientId && result.patientId === patientId && result.status === 'completed'
    && result.resultId !== resultId && !['ASSESSMENT_REPORT', 'COMPOSITE_REPORT'].includes(result.testKey)),
  [sourceResults, clientId, patientId, resultId]);
  const testResults = eligibleResults.filter((result) => result.testKey !== 'INITIAL_ASSESSMENT');
  const baselineResults = eligibleResults.filter((result) => result.testKey === 'INITIAL_ASSESSMENT');
  const filteredResults = testResults.filter((result) =>
    `${getAssessmentV2Label(result.testKey)} ${formatInitialAssessmentDate(result.assessmentDate)}`
      .toLocaleLowerCase('pt-BR').includes(sourceSearch.trim().toLocaleLowerCase('pt-BR')));
  const sourceIssues = sourceResults === undefined || sourceResultsLoading ? [] : getInitialAssessmentSourceIssues(form.selectedResults, sourceResults, { clientId: clientId ?? '', patientId: patientId ?? '' });

  const buildPayload = (): InitialAssessmentResultPayload => ({
    schemaVersion: INITIAL_ASSESSMENT_SCHEMA_VERSION,
    ...form,
    finishedAt: new Date().toISOString(),
  });

  const pendingAnamnesis = () => {
    if (savingAnamnesis || (showAnamnesisCreator && hasAnamnesisContent(newAnamnesisSections))) {
      setValidationError('Salve ou cancele a nova anamnese antes de salvar a avaliação.');
      setActiveStep(0);
      return true;
    }
    return false;
  };

  const saveDraft = async () => {
    if (!onSaveDraft || pendingAnamnesis()) return;
    const payload = normalizeInitialAssessmentPayload(buildPayload());
    if (!payload) {
      setValidationError('Registre uma informação ou inclua a anamnese antes de salvar o rascunho.');
      return;
    }
    setSavingDraft(true);
    setValidationError(null);
    try { await onSaveDraft(payload); }
    catch (error) { setValidationError(error instanceof Error ? error.message : 'Não foi possível salvar o rascunho.'); }
    finally { setSavingDraft(false); }
  };

  const submit = () => {
    if (pendingAnamnesis()) return;
    if (!form.assessmentReason.trim()) {
      setValidationError('Informe o motivo da avaliação.');
      setActiveStep(0);
      return;
    }
    if (!form.professionalSynthesis.trim()) {
      setValidationError('Registre a síntese da avaliação profissional.');
      setActiveStep(3);
      return;
    }
    if (sourceResultsLoading || sourceIssues.length) {
      setValidationError(sourceResultsLoading ? 'Aguarde a consulta dos resultados selecionados.' : 'Revise os testes de origem indisponíveis ou alterados: remova-os e selecione a versão atual.');
      setActiveStep(2);
      return;
    }
    const normalized = normalizeInitialAssessmentPayload(buildPayload());
    if (normalized) onNext(normalized);
  };

  const steps = ['História', 'Exame', 'Testes', 'Síntese e plano', 'Revisão'];

  const toggleSource = (result: AssessmentResultV2) => {
    if (!buildSourceSnapshot) return;
    setValidationError(null);
    setForm((current) => ({ ...current, selectedResults: current.selectedResults.some((source) => source.resultId === result.resultId)
      ? current.selectedResults.filter((source) => source.resultId !== result.resultId)
      : [...current.selectedResults, buildSourceSnapshot(result)] }));
  };

  return (
    <div className="space-y-5">
      <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-primary-50 p-3 text-primary-700">
            <ClipboardCheck className="h-6 w-6" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-primary-700">Ficha clínica</div>
            <h2 className="mt-1 text-xl font-bold text-slate-900">Avaliação fisioterapêutica integrada</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Reúna história, exame, medidas e sua síntese clínica. Você pode preencher em etapas e revisar os dados antes de concluir.
            </p>
          </div>
        </div>
      </div>

      <nav aria-label="Etapas da avaliação" className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-3">
        {steps.map((step, index) => <button key={step} type="button" aria-current={activeStep === index ? 'step' : undefined} onClick={() => setActiveStep(index)} className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${activeStep === index ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{index + 1}. {step}</button>)}
      </nav>

      <div hidden={activeStep !== 0} className="space-y-5">
      <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Contexto da avaliação</div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="text-sm font-semibold text-slate-800">Momento<select value={form.assessmentMoment} onChange={(event) => update('assessmentMoment', event.target.value as InitialAssessmentMoment)} className={textAreaClassName}>{Object.entries(INITIAL_ASSESSMENT_MOMENT_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
          <label className="text-sm font-semibold text-slate-800">Episódio ou programa de cuidado<input value={form.episodeLabel} onChange={(event) => update('episodeLabel', event.target.value)} className={textAreaClassName} placeholder="Ex.: reabilitação respiratória — 2026" /></label>
          {form.assessmentMoment !== 'initial' ? <label className="text-sm font-semibold text-slate-800 md:col-span-2">Avaliação de referência<select value={form.baselineResultId ?? ''} onChange={(event) => update('baselineResultId', event.target.value || null)} className={textAreaClassName}><option value="">Selecionar se houver avaliação anterior</option>{baselineResults.map((result) => <option key={result.resultId} value={result.resultId}>{formatInitialAssessmentDate(result.assessmentDate)} · {result.payloadJson.episodeLabel || result.payloadJson.assessmentReason || 'Avaliação clínica'}</option>)}{form.baselineResultId && !baselineResults.some((result) => result.resultId === form.baselineResultId) ? <option value={form.baselineResultId}>Referência previamente vinculada</option> : null}</select></label> : null}
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="text-sm font-semibold text-slate-800">
            Motivo da avaliação <span className="text-rose-600">*</span>
            <textarea
              value={form.assessmentReason}
              onChange={(event) => update('assessmentReason', event.target.value)}
              rows={3}
              className={textAreaClassName}
              placeholder="Motivo do encaminhamento ou objetivo desta avaliação."
            />
          </label>
          <label className="text-sm font-semibold text-slate-800">
            Encaminhamento ou diagnóstico informado
            <textarea
              value={form.referralDiagnosis}
              onChange={(event) => update('referralDiagnosis', event.target.value)}
              rows={3}
              className={textAreaClassName}
              placeholder="Registre a informação recebida, sem gerar diagnóstico automático."
            />
          </label>
          <label className="text-sm font-semibold text-slate-800 md:col-span-2">
            Condição clínica atual
            <textarea
              value={form.currentClinicalCondition}
              onChange={(event) => update('currentClinicalCondition', event.target.value)}
              rows={3}
              className={textAreaClassName}
              placeholder="Estado atual, evolução recente e informações clínicas relevantes para a avaliação."
            />
          </label>
        </div>
      </section>

      <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Estado clínico-funcional</div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="text-sm font-semibold text-slate-800 md:col-span-2">Funcionalidade anterior<textarea value={form.previousFunction} onChange={(event) => update('previousFunction', event.target.value)} rows={3} className={textAreaClassName} placeholder="O que fazia antes deste quadro? Considere atividades, participação e apoios necessários." /></label>
          <label className="text-sm font-semibold text-slate-800">
            Sintomas e queixas atuais
            <textarea value={form.mainSymptoms} onChange={(event) => update('mainSymptoms', event.target.value)} rows={4} className={textAreaClassName} />
          </label>
          <label className="text-sm font-semibold text-slate-800">
            Funcionalidade atual e limitações de participação
            <textarea value={form.functionalLimitations} onChange={(event) => update('functionalLimitations', event.target.value)} rows={4} className={textAreaClassName} />
          </label>
          <label className="text-sm font-semibold text-slate-800">
            Dispositivos, órteses e suporte em uso
            <textarea value={form.assistiveDevices} onChange={(event) => update('assistiveDevices', event.target.value)} rows={3} className={textAreaClassName} />
          </label>
          <label className="text-sm font-semibold text-slate-800">
            Precauções e restrições relevantes
            <textarea value={form.precautions} onChange={(event) => update('precautions', event.target.value)} rows={3} className={textAreaClassName} />
          </label>
          <label className="text-sm font-semibold text-slate-800 md:col-span-2">
            O que o paciente quer recuperar, manter ou adaptar?
            <textarea value={form.patientGoals} onChange={(event) => update('patientGoals', event.target.value)} rows={3} className={textAreaClassName} />
          </label>
        </div>
      </section>

      <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <FileHeart className="h-4 w-4 text-primary-700" />
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Anamnese do prontuário</div>
          </div>
          {onCreateAnamnesis && !showAnamnesisCreator ? (
            <button
              type="button"
              onClick={() => {
                setValidationError(null);
                setAnamnesisCreationError(null);
                setShowAnamnesisCreator(true);
              }}
              className="self-start rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-100 sm:self-auto"
            >
              Registrar nova anamnese
            </button>
          ) : null}
        </div>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Escolha qual versão será copiada para esta avaliação. O registro original do prontuário não será alterado.
        </p>

        {showAnamnesisCreator ? (
          <div className="mt-4 rounded-2xl border border-primary-200 bg-primary-50/40 p-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Nova anamnese</h3>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Ao salvar, uma nova versão será registrada em Evoluções e incluída nesta avaliação.
              </p>
            </div>

            <label className="mt-4 block text-sm font-semibold text-slate-800">
              Data e hora da anamnese
              <input
                type="datetime-local"
                value={newAnamnesisDate}
                onChange={(event) => {
                  setAnamnesisCreationError(null);
                  setNewAnamnesisDate(event.target.value);
                }}
                disabled={savingAnamnesis}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-700 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:bg-slate-100 md:max-w-sm"
              />
            </label>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {ANAMNESIS_SECTION_DEFINITIONS.map(({ key, label, placeholder }) => (
                <label
                  key={key}
                  className={`text-sm font-semibold text-slate-800 ${key === 'additionalNotes' ? 'md:col-span-2' : ''}`}
                >
                  {label}
                  <textarea
                    value={newAnamnesisSections[key]}
                    onChange={(event) => updateNewAnamnesisSection(key, event.target.value)}
                    rows={key === 'currentIllnessHistory' ? 4 : 3}
                    placeholder={placeholder}
                    disabled={savingAnamnesis}
                    className={`${textAreaClassName} disabled:bg-slate-100`}
                  />
                </label>
              ))}
            </div>

            {anamnesisCreationError ? (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
                {anamnesisCreationError}
              </div>
            ) : null}

            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeAnamnesisCreator}
                disabled={savingAnamnesis}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void saveNewAnamnesis()}
                disabled={savingAnamnesis}
                className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingAnamnesis ? 'Salvando anamnese...' : 'Salvar no prontuário e incluir'}
              </button>
            </div>
          </div>
        ) : null}

        {requestedAnamnesisUnavailable ? <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">A anamnese solicitada não está disponível. Confira o prontuário e escolha uma versão válida para incluir nesta avaliação.</p> : null}

        {loadingAnamnesis ? (
          <p className="mt-3 text-sm text-slate-500">Consultando as anamneses do prontuário...</p>
        ) : anamnesisLoadError && !form.anamnesisSnapshot ? (
          <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
            <span>Não foi possível consultar as anamneses do paciente.</span>
            <button
              type="button"
              onClick={() => setAnamnesisReloadKey((current) => current + 1)}
              className="self-start rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-100 sm:self-auto"
            >
              Tentar novamente
            </button>
          </div>
        ) : displayedAnamnesisOptions.length ? (
          <div className="mt-4 space-y-4">
            {anamnesisLoadError ? (
              <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
                <span>A anamnese já vinculada foi preservada, mas não foi possível consultar outras versões.</span>
                <button
                  type="button"
                  onClick={() => setAnamnesisReloadKey((current) => current + 1)}
                  className="self-start rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-100 sm:self-auto"
                >
                  Tentar novamente
                </button>
              </div>
            ) : null}
            <label className="block text-sm font-semibold text-slate-800">
              Anamnese incluída na avaliação
              <select
                value={form.anamnesisSnapshot?.sourceRecordId ?? ''}
                onChange={(event) => selectAnamnesis(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-700 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Não incluir anamnese nesta avaliação</option>
                {displayedAnamnesisOptions.map((option) => (
                  <option key={option.snapshot.sourceRecordId} value={option.snapshot.sourceRecordId}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {form.anamnesisSnapshot ? (
              <div className="rounded-2xl border border-teal-100 bg-teal-50/50 p-4">
                <p className="text-xs font-semibold text-teal-800">
                  {form.anamnesisSnapshot.sourceRecordTitle || 'Anamnese'} • versão{' '}
                  {form.anamnesisSnapshot.documentVersion} •{' '}
                  {formatInitialAssessmentDate(form.anamnesisSnapshot.sourceRecordDate)}
                </p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {anamnesisEntries.map((entry) => (
                    <div key={entry.label} className="rounded-2xl bg-white px-4 py-3">
                      <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{entry.label}</div>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">{entry.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                A avaliação será salva sem uma cópia da anamnese.
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3 flex items-start gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            Nenhuma anamnese ativa foi encontrada no prontuário deste paciente. A avaliação pode ser concluída sem esse vínculo.
          </div>
        )}
      </section>

      </div>

      <div hidden={activeStep !== 1} className="space-y-5">
        <InitialAssessmentPhysicalExaminationEditor value={form.physicalExamination} onChange={(value) => update('physicalExamination', value)} />
        <InitialAssessmentExternalExamsEditor clientId={clientId} patientId={patientId} value={form.externalExams} onChange={(value) => update('externalExams', value)} />
      </div>

      <div hidden={activeStep !== 2} className="space-y-5">
        <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-bold text-slate-900">Testes e medidas desta avaliação</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">Inclua resultados concluídos do paciente que sustentam esta avaliação. Cada seleção preserva a data, o resultado e a referência da aplicação.</p>
          {sourceResultsLoading ? <p className="mt-4 text-sm text-slate-500">Consultando resultados do paciente...</p> : null}
          {sourceIssues.length ? <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Há resultados de origem indisponíveis ou alterados. Remova a seleção anterior e inclua a versão que deseja revisar.</p> : null}
          {form.selectedResults.length ? <div className="mt-4 space-y-2">{form.selectedResults.map((source) => <div key={source.resultId} className="flex items-start justify-between gap-3 rounded-xl border border-primary-100 bg-primary-50/40 p-3"><div><div className="text-sm font-semibold text-slate-800">{source.snapshot.testLabel}</div><div className="mt-1 text-xs text-slate-600">{formatInitialAssessmentDate(source.snapshot.assessmentDate)} · {source.snapshot.primaryMeasure.formattedValue}</div></div><button type="button" onClick={() => update('selectedResults', form.selectedResults.filter((item) => item.resultId !== source.resultId))} className="rounded-lg px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50">Remover</button></div>)}</div> : null}
          {buildSourceSnapshot ? <><label className="mt-4 block text-sm font-semibold text-slate-700">Buscar por instrumento ou data<input value={sourceSearch} onChange={(event) => setSourceSearch(event.target.value)} className={textAreaClassName} placeholder="Ex.: caminhada, PImax, 2026" /></label><div className="mt-4 max-h-80 space-y-2 overflow-y-auto">{filteredResults.map((result) => <label key={result.resultId} className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3 text-sm hover:bg-slate-50"><input type="checkbox" checked={form.selectedResults.some((source) => source.resultId === result.resultId)} onChange={() => toggleSource(result)} className="mt-1 h-4 w-4 rounded border-slate-300 text-primary-600" /><span><span className="font-semibold text-slate-800">{getAssessmentV2Label(result.testKey)}</span><span className="mt-1 block text-xs text-slate-500">{formatInitialAssessmentDate(result.assessmentDate)} · {result.professionalNameSnapshot || 'Profissional não informado'}</span></span></label>)}{!filteredResults.length && !sourceResultsLoading ? <p className="text-sm text-slate-500">Nenhum resultado concluído encontrado para esta seleção.</p> : null}</div></> : <p className="mt-4 text-sm text-slate-500">Os testes podem ser aplicados na mesma sessão. Depois de salvos, ficam disponíveis para compor o relatório desta avaliação.</p>}
        </section>
        {onRequestTests ? <InitialAssessmentTestPlanning
          ageYears={patientContext?.ageYears ?? (typeof initial?.patientContext?.ageYears === 'number' ? initial.patientContext.ageYears : null)}
          selectedKeys={requestedTests}
          onSelectionChange={setRequestedTests}
          onRequest={onRequestTests}
        /> : null}
      </div>

      <div hidden={activeStep !== 3} className="space-y-5">
      <InitialAssessmentGoalsEditor value={form.measurableGoals} onChange={(value) => update('measurableGoals', value)} />
      <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Síntese e conduta</div>
        <div className="mt-4 space-y-4">
          <label className="block text-sm font-semibold text-slate-800">
            Síntese da avaliação profissional <span className="text-rose-600">*</span>
            <textarea
              value={form.professionalSynthesis}
              onChange={(event) => update('professionalSynthesis', event.target.value)}
              rows={5}
              className={textAreaClassName}
              placeholder="Integre os achados relevantes sem transformar o registro em diagnóstico automático."
            />
          </label>
          <label className="block text-sm font-semibold text-slate-800">Prognóstico fisioterapêutico<textarea value={form.prognosis} onChange={(event) => update('prognosis', event.target.value)} rows={3} className={textAreaClassName} placeholder="Registre o resultado funcional esperado, prazo e fatores que influenciam a evolução." /></label>
          <label className="block text-sm font-semibold text-slate-800">
            Plano terapêutico
            <textarea value={form.initialPlan} onChange={(event) => update('initialPlan', event.target.value)} rows={4} className={textAreaClassName} />
          </label>
          <label className="block text-sm font-semibold text-slate-800">Seguimento e reavaliação<textarea value={form.followUpPlan} onChange={(event) => update('followUpPlan', event.target.value)} rows={3} className={textAreaClassName} placeholder="Frequência, previsão de atendimentos, medidas a repetir e quando revisar." /></label>
          {form.assessmentMoment === 'final' ? <label className="block text-sm font-semibold text-slate-800">Motivo do encerramento e orientações<textarea value={form.closureReason} onChange={(event) => update('closureReason', event.target.value)} rows={3} className={textAreaClassName} /></label> : null}
          <div className="rounded-2xl border border-primary-100 bg-primary-50/40 p-4 text-sm leading-6 text-slate-700"><strong>Diagnóstico fisioterapêutico e CBDF</strong><p className="mt-1">Ao gerar o relatório técnico a partir desta avaliação, você poderá registrar e confirmar a CBDF vinculada aos achados e testes selecionados, com autoria profissional.</p></div>
          <label className="block text-sm font-semibold text-slate-800">
            Observações complementares
            <textarea value={form.additionalNotes} onChange={(event) => update('additionalNotes', event.target.value)} rows={3} className={textAreaClassName} />
          </label>
        </div>
      </section>

      </div>

      <div hidden={activeStep !== 4} className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-600">Confira o momento da avaliação, a versão da anamnese e os resultados selecionados. Campos opcionais vazios serão omitidos do documento.</div>
        <InitialAssessmentResultRenderer payload={buildPayload()} />
      </div>

      {validationError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">{validationError}</div>
      ) : null}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {onCancel ? (
          <button type="button" onClick={onCancel} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancelar
          </button>
        ) : null}
        {onSaveDraft && !isEditing ? <button type="button" disabled={savingDraft || savingAnamnesis} onClick={() => void saveDraft()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary-200 bg-white px-4 py-2.5 text-sm font-semibold text-primary-700 disabled:opacity-50"><Save className="h-4 w-4" />{savingDraft ? 'Salvando...' : 'Salvar rascunho'}</button> : null}
        {activeStep > 0 ? <button type="button" onClick={() => setActiveStep((current) => current - 1)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700"><ChevronLeft className="h-4 w-4" />Anterior</button> : null}
        {activeStep < steps.length - 1 ? <button type="button" onClick={() => setActiveStep((current) => current + 1)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700">Continuar<ChevronRight className="h-4 w-4" /></button> : <button type="button" disabled={savingDraft || savingAnamnesis} onClick={submit} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"><Check className="h-4 w-4" />{isEditing ? 'Salvar alterações' : 'Concluir avaliação'}</button>}
      </div>
    </div>
  );
};

export type { InitialAssessmentResultPayload };
export default InitialAssessmentTest;
